import { motion } from "framer-motion";
import { CheckCircle2, Users, Target, MessageSquare, Heart, Lightbulb, BookOpen, ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function JegTilbyder() {
  const offerings = [
    "Foredrag til sundhedsprofessionelle og tværfaglige teams",
    "Senfølger efter seksuelle overgreb i barndommen",
    "Psykiske- og somatiske lidelser i sundhedsvæsenet",
    "Patientperspektiv og fortællinger fra egne erfaringer",
    "Kommunikation, recovery og tryghed i mødet",
    "Kommunikation med psykisksårbare patienter",
    "Den svære samtale – Træning i ro og tydelighed",
    "Humor og selvindsigt i patientforløb",
    "Tværfaglig kommunikation og fælles sprog",
    "Når ord gør en forskel",
    "Redskaber, når patienten bryder sammen",
    "Fra idé til virkelighed"
  ];

  const targetGroups = [
    "Læger",
    "Sygeplejersker",
    "SOSU´er",
    "Alle former af Terapeuter: Fysioterapeuter, Kiropraktorer osv.",
    "Studerende",
    "Tværfaglige Teams",
    "Politi",
    "Politikere",
    "Skoler",
    "(Alle som har en  interesse i at bryde tabuer)"
  ];

  const benefits = [
    "Konkrete samtaleredskaber",
    "Styrket empati & samarbejde",
    "Et fælles sprog i teamet",
    "Et indblik i den traumebevidste tilgang (TBT)",
    "Et indblik i hvad senfølger er",
    "En forståelse, hvorfor \"noget\" er sværere for psykisk sårbare",
    "Et svar på, hvorfor en somatisk undersøgelse er umulig for senfølgeramte",
    "Hvad er det der trikker….",
    "Hvordan du hjælper dig selv til at forstå…"
  ];

  const formats = [
    {
      title: "Oplæg",
      desc: "Skræddersyet emner inden for recovery i psykiatri, senfølger, K-PTSD, alkohol m.m"
    },
    {
      title: "Foredrag",
      desc: "Inspirerende & reflekterende"
    },
    {
      title: "Introduktion til hvordan *SeneseBridge anvendes",
      desc: "SenseBridge er et nyt redskab til din sårbare borger/patient"
    }
  ];

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8 }
  };

  return (
    <div className="pt-24 pb-12">
      <Helmet>
        <title>Det tilbyder jeg: Oplæg, foredrag & SenseBridge – Ta' JA-Hatten På!</title>
        <meta name="description" content="Skræddersyede oplæg og foredrag til sundhedsprofessionelle, tværfaglige teams, studerende og pårørende. Lær om SenseBridge og nærværende kommunikation." />
      </Helmet>

      {/* Hero Section */}
      <section className="container mx-auto px-4 md:px-6 mb-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div {...fadeIn}>
            <span className="text-red-600 font-bold tracking-[0.2em] uppercase text-sm mb-4 block">
              Skræddersyede løsninger
            </span>
            <h1 className="text-4xl md:text-6xl font-serif italic text-slate-900 mb-8 leading-tight">
              Jeg tilbyder...
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
              Alle foredrag bliver tilrettelagt ud fra netop jeres ønsker. Der er derfor ingen foredrag som er ens.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="px-8 py-4 bg-slate-900 text-white font-bold tracking-widest uppercase text-sm hover:bg-red-600 transition-colors duration-500"
              >
                Book en samtale
              </Link>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative aspect-[4/5] md:h-[500px] md:aspect-auto overflow-hidden shadow-2xl"
          >
            <img 
              src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/d19c68f5-f69c-40ae-bab6-c731c9ce6903.jpg" 
              alt="Candra Gaarde" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 border-[20px] border-white/10 pointer-events-none" />
          </motion.div>
        </div>
      </section>

      {/* Emner & Tilbud */}
      <section className="container mx-auto px-4 md:px-6 mb-16 lg:mb-20">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <h2 className="text-3xl font-serif italic mb-6">Emner jeg dækker</h2>
            <p className="text-slate-600 mb-8">
              Jeg tilbyder bl.a. et anderledes foredrag, hvor historien bag "Ta' Ja-Hatten På!" blev født. 
              Dette oplæg sætter fokus på den menneskelige side af sundhedsvæsenet med humor og selvindsigt.
            </p>
            
            {/* Integreret Vigtigt at vide */}
            <div className="mb-8 p-6 bg-slate-50 border border-slate-200">
              <h3 className="font-serif italic text-lg text-slate-900 mb-3">VIGTIGT AT VIDE:</h3>
              <div className="text-sm text-slate-600 space-y-2 leading-relaxed">
                <p>Alle foredrag bliver tilrettelagt ud fra netop jeres ønsker.</p>
                <p>Der er derfor ingen foredrag som er ens.</p>
                <p>Varighed af foredrag samt priser, er derfor variable.</p>
              </div>
            </div>

            <div className="p-8 bg-red-50 border-l-4 border-red-600">
              <p className="italic text-red-900">
                "Et inspirerende oplæg, der giver stof til refleksion for både personale, ledere, studerende og pårørende."
              </p>
            </div>
          </div>
          <div className="lg:col-span-2 grid md:grid-cols-2 gap-6">
            {offerings.map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start gap-4 p-4 bg-slate-50/50 border border-slate-100 h-full">
                  <CheckCircle2 className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                  <span className="text-slate-700 font-medium">
                    {item}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SenseBridge Section */}
      <section className="w-full bg-slate-950 text-white py-20 md:py-28 mb-20 relative overflow-hidden">
        {/* Subtle background decorative elements */}
        <div className="absolute top-1/4 -left-40 w-96 h-96 bg-red-900/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-slate-800/20 rounded-full blur-3xl pointer-events-none" />
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-3xl mx-auto space-y-8">
            <div>
              <span className="text-red-500 font-bold tracking-[0.2em] uppercase text-xs mb-3 block">
                Eget udviklet kommunikationsværktøj
              </span>
              <h2 className="text-4xl md:text-5xl font-serif italic text-white leading-tight">
                SenseBridge
              </h2>
              <div className="w-20 h-1 bg-red-600 mt-6 rounded-full" />
            </div>

            <div className="space-y-6 text-slate-300 text-base md:text-lg leading-relaxed">
              <p className="border-l-4 border-red-600/30 pl-4 py-1 italic text-slate-200 text-lg md:text-xl">
                SenseBridge er et kommunikations – redskab/værktøj som jeg har udviklet, efter mange uheldige situationer med fagpersoner.
              </p>
              <p>
                Situationer hvor kommunikationen mellem fagpersoner og jeg selv som borger/patient er gået skævt. Dette har både været en ubehagelig oplevelse for både fagpersonen og mig selv.
              </p>
              <p>
                Det fik mig til at arbejde med idéen på, hvad jeg selv kunne have haft brug for, i den givne situation som kunne have hjulpet med at forbedre kommunikationen, men i særdeleshed også hvordan jeg kunne sige fra, til noget ubehageligt, men samtidig også hjælpe fagpersonerne med andre muligheder for at gennemføre deres arbejde.
              </p>
              <p>
                SenseBridge er lavet så det passer til alle målgrupper, og ikke kun psykisk sårbare, men alle som pludselig står i en sårbar situation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Målgruppe, Udbytte & Formater */}
      <section className="container mx-auto px-4 md:px-6 mb-20">
        <div className="grid lg:grid-cols-3 gap-12 lg:gap-8 xl:gap-12">
          {/* Målgruppe */}
          <motion.div {...fadeIn}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-emerald-50 flex items-center justify-center rounded-full">
                <Users className="text-emerald-600" />
              </div>
              <h2 className="text-3xl font-serif italic">MÅLGRUPPE</h2>
            </div>
            <div className="space-y-3">
              {targetGroups.map((group, idx) => (
                <div key={idx} className="flex items-start gap-2 text-slate-600 leading-relaxed">
                  <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <span>{group}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Udbytte */}
          <motion.div {...fadeIn}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-amber-50 flex items-center justify-center rounded-full">
                <Target className="text-amber-500" />
              </div>
              <h2 className="text-3xl font-serif italic">UDBYTTE</h2>
            </div>
            <ul className="space-y-3">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-start gap-3 text-slate-600 leading-relaxed">
                  <span className="text-amber-500 font-bold">/</span>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Formater */}
          <motion.div {...fadeIn}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-red-50 flex items-center justify-center rounded-full">
                <BookOpen className="text-red-600" />
              </div>
              <h2 className="text-3xl font-serif italic">FORMATER</h2>
            </div>
            <div className="space-y-6">
              {formats.map((format, idx) => (
                <div key={idx} className="border-l-2 border-red-600 pl-4 py-1">
                  <h3 className="font-serif italic text-lg text-slate-900 mb-1">{format.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{format.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Galleri / Billeder fra scrape */}
      <section className="container mx-auto px-4 md:px-6 mb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            "https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/dd925492-3606-4896-a3b2-dbf1e0c23def.jpg",
            "https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/545ef2f0-eba3-4b5d-b56b-dd7d75267067.jpg",
            "https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/35a184eb-9f88-4df4-9457-f727b47cf630.jpg"
          ].map((src, idx) => (
            <motion.div 
              key={idx}
              whileHover={{ scale: 1.02 }}
              className="aspect-square overflow-hidden shadow-lg"
            >
              <img src={src} alt={`Galleri ${idx}`} className="w-full h-full object-cover" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto bg-slate-50 border border-slate-200 p-8 md:p-12 lg:p-16 text-center">
          <h2 className="text-3xl font-serif italic mb-8">Spørgsmål & Booking</h2>
          <p className="text-lg md:text-xl text-slate-800 font-medium mb-4">
            ALT vil blive skræddersyet til netop jeres behov, ønsker og målgruppe
          </p>
          <p className="text-slate-600 mb-12 max-w-2xl mx-auto">
            Har du spørgsmål og ønsker at vide mere, herunder priser og booking udfyld kontakt formular
          </p>
          <Link
            to="/kontakt"
            className="inline-block px-12 py-5 bg-red-600 text-white font-bold tracking-widest uppercase hover:bg-slate-900 transition-colors duration-500"
          >
            Udfyld kontaktformular
          </Link>
        </div>
      </section>
    </div>
  );
}
